import { apiClient } from "@/services/apiClient";
import type {
  GraphState,
  ProductGraph,
} from "@/services/platformEnterprisesService";

export interface EnterpriseStaffRequestView {
  id: string;
  enterpriseId: string;
  userId: string;
  productSlug: string;
  status: string;
  payload: Record<string, unknown>;
  workflowState: GraphState | null;
  graph?: ProductGraph | null;
  product?: { id: string; displayName: string } | null;
  user?: { id: string; firstName?: string | null; lastName?: string | null; email: string } | null;
  computedPriceMinor: number | null;
  computedPriceCurrency: string | null;
  parentRequestId: string | null;
  parentRequestType: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EnterpriseRequestEvent {
  id: string;
  requestId: string;
  type: string;
  data: Record<string, unknown>;
  actorUserId: string | null;
  createdAt: string;
}

function tenantHeaders(subdomain: string) {
  return { headers: { "X-Enterprise-Subdomain": subdomain } };
}

export interface DashboardActionItem {
  id: string;
  productName: string;
  userName: string;
  userEmail: string;
  status: string;
  currentStepLabel: string | null;
  createdAt: string;
}

export interface DashboardEvent {
  id: string;
  requestId: string;
  type: string;
  data: Record<string, unknown>;
  actorUserId: string | null;
  createdAt: string;
  requestProductName: string | null;
  requestUserName: string | null;
}

export interface EnterpriseDashboardData {
  stats: Record<string, number>;
  actionNeeded: DashboardActionItem[];
  recentEvents: DashboardEvent[];
}

export async function getEnterpriseDashboard(
  subdomain: string,
): Promise<EnterpriseDashboardData> {
  const res = await apiClient.get<EnterpriseDashboardData>(
    "/enterprise/me/requests/dashboard",
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function listStaffRequests(
  subdomain: string,
  params?: { status?: string },
): Promise<EnterpriseStaffRequestView[]> {
  const res = await apiClient.get<{
    items: EnterpriseStaffRequestView[];
    total: number;
  }>(
    "/enterprise/me/requests",
    { params, ...tenantHeaders(subdomain) },
  );
  return res.data.items;
}

export async function getStaffRequest(
  subdomain: string,
  id: string,
): Promise<EnterpriseStaffRequestView> {
  const res = await apiClient.get<EnterpriseStaffRequestView>(
    `/enterprise/me/requests/${encodeURIComponent(id)}`,
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function getStaffRequestEvents(
  subdomain: string,
  id: string,
): Promise<EnterpriseRequestEvent[]> {
  const res = await apiClient.get<EnterpriseRequestEvent[]>(
    `/enterprise/me/requests/${encodeURIComponent(id)}/events`,
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function staffCompleteStep(
  subdomain: string,
  requestId: string,
  stepKey: string,
  data?: Record<string, unknown>,
): Promise<EnterpriseStaffRequestView> {
  const res = await apiClient.post<EnterpriseStaffRequestView>(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/complete-step`,
    { stepKey, data },
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function staffRejectRequest(
  subdomain: string,
  requestId: string,
  reason?: string,
): Promise<void> {
  await apiClient.post(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/reject`,
    { reason },
    tenantHeaders(subdomain),
  );
}

export async function staffRequestInfo(
  subdomain: string,
  requestId: string,
  message?: string,
): Promise<void> {
  await apiClient.post(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/request-info`,
    { message },
    tenantHeaders(subdomain),
  );
}

export async function staffPickUpRequest(
  subdomain: string,
  requestId: string,
): Promise<void> {
  await apiClient.post(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/pick-up`,
    {},
    tenantHeaders(subdomain),
  );
}

export async function staffUploadDocument(
  subdomain: string,
  requestId: string,
  nodeId: string,
  file: File,
): Promise<EnterpriseStaffRequestView> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<EnterpriseStaffRequestView>(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/upload-document/${encodeURIComponent(nodeId)}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Enterprise-Subdomain": subdomain,
      },
    },
  );
  return res.data;
}

export async function staffUploadCollectionDocument(
  subdomain: string,
  requestId: string,
  nodeId: string,
  documentKey: string,
  file: File,
): Promise<EnterpriseStaffRequestView> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<EnterpriseStaffRequestView>(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/upload-collection-doc/${encodeURIComponent(nodeId)}?documentKey=${encodeURIComponent(documentKey)}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-Enterprise-Subdomain": subdomain,
      },
    },
  );
  return res.data;
}

export function staffDocumentUrl(
  subdomain: string,
  requestId: string,
  nodeId: string,
): string {
  return `/enterprise/me/requests/${encodeURIComponent(requestId)}/document/${encodeURIComponent(nodeId)}`;
}

export async function fetchStaffDocumentBlob(
  subdomain: string,
  requestId: string,
  nodeId: string,
): Promise<Blob> {
  const res = await apiClient.get(
    staffDocumentUrl(subdomain, requestId, nodeId),
    { responseType: "blob", ...tenantHeaders(subdomain) },
  );
  return res.data as Blob;
}

export interface ChildRequestDocument {
  id: string;
  fileName: string;
  url: string;
}

export interface ChildRequestSummary {
  stepKey: string;
  childRequestId: string;
  type: string;
  status: string;
  documents?: ChildRequestDocument[];
}

export interface StaffRequestParticipant {
  id: string;
  enterpriseRequestId: string;
  userId: string | null;
  email: string;
  role: 'initiator' | 'participant';
  status: 'pending' | 'accepted' | 'declined';
  inviteNodeId: string | null;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  } | null;
  createdAt: string;
}

export async function getStaffRequestParticipants(
  subdomain: string,
  requestId: string,
): Promise<StaffRequestParticipant[]> {
  const res = await apiClient.get<StaffRequestParticipant[]>(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/participants`,
    tenantHeaders(subdomain),
  );
  return res.data;
}

export async function getStaffChildRequests(
  subdomain: string,
  requestId: string,
): Promise<ChildRequestSummary[]> {
  const res = await apiClient.get<ChildRequestSummary[]>(
    `/enterprise/me/requests/${encodeURIComponent(requestId)}/child-requests`,
    tenantHeaders(subdomain),
  );
  return res.data;
}
